import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addMemberPaymentCard, logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { AddPaymentCardErrorResultCode } from '@vista-digital/embedded-payments';
import { useMountEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { useCallback, useContext, useEffect, useId, useRef } from 'react';
import { Button } from '../../../subcomponents/button/button';
import { ButtonColor } from '../../../subcomponents/button/types/color';
import { ButtonSize } from '../../../subcomponents/button/types/size';
import { CheckboxField } from '../../../subcomponents/form/field/checkbox/checkbox-field';
import { getFieldId } from '../../../subcomponents/form/field/utils/field-attribute-utils';
import { LoadingIndicator } from '../../../subcomponents/loading-indicator/loading-indicator';
import { LoadingIndicatorType } from '../../../subcomponents/loading-indicator/types/type';
import { MemberPaymentCardWalletContextInstance } from '../types/context';
import { createPaymentCardsClient } from '../utils/create-payment-cards-client';
import { MemberPaymentCardWalletFormActionType } from '../utils/form/form-action-type';
import { MemberPaymentCardWalletFormLoadingState } from '../utils/form/form-state';
import { MemberPaymentCardWalletMessageType } from './message';
const b = bemClassBuilder('v-member-payment-card-wallet-connector');
const propsByComponentId = {};
export const MemberPaymentCardWalletConnector = observer(props => {
    const { paymentMethodId, loadingState, onClose, onAddPaymentCard, dispatch, termsAndConditionsAccepted, addPaymentCardButtonState, showAddCardButton, showCancelButton } = props;
    const componentId = useId();
    useEffect(() => {
        propsByComponentId[componentId] = props;
        return () => {
            delete propsByComponentId[componentId];
        };
    }, [props, componentId]);
    const { displayText, assets, config, events, onCardAdded } = useContext(MemberPaymentCardWalletContextInstance);
    const { addingPaymentCardMessage, addCardTermsAndConditionsFieldLabel, promptCancelButtonLabel, addCardSubmitButtonLabel } = displayText;
    const { onAddPaymentCardFailed, onAddPaymentCardSuccessful } = events;
    const containerRef = useRef(null);
    const handleAddPaymentCardFailed = useCallback((paymentError) => {
        if (paymentError.resultCode === AddPaymentCardErrorResultCode.CardDeclined) {
            dispatch({
                type: MemberPaymentCardWalletFormActionType.SetError,
                error: MemberPaymentCardWalletMessageType.CardDeclined
            });
        }
        else {
            dispatch({
                type: MemberPaymentCardWalletFormActionType.SetError,
                error: MemberPaymentCardWalletMessageType.AddCardFailed
            });
        }
        onAddPaymentCardFailed === null || onAddPaymentCardFailed === void 0 ? void 0 : onAddPaymentCardFailed(paymentError);
    }, [dispatch, onAddPaymentCardFailed]);
    const handleAddPaymentCardSuccessful = useCallback(async (paymentCard) => {
        var _a;
        addMemberPaymentCard(paymentCard);
        await onCardAdded(paymentCard, !!((_a = propsByComponentId[componentId]) === null || _a === void 0 ? void 0 : _a.makePrimaryChosen));
        onAddPaymentCardSuccessful === null || onAddPaymentCardSuccessful === void 0 ? void 0 : onAddPaymentCardSuccessful(paymentCard);
        onClose();
    }, [componentId, onAddPaymentCardSuccessful, onCardAdded, onClose]);
    const handleTermsAndConditionFieldChanged = (value) => dispatch({ type: MemberPaymentCardWalletFormActionType.TermsAndConditionsAccepted, accepted: value });
    useMountEffect(() => {
        (async () => {
            try {
                const client = createPaymentCardsClient({
                    paymentMethodId,
                    containerRef,
                    childContainerClassName: b.element('child-container'),
                    redirectReturnUrl: config.redirectReturnUrl,
                    onAddPaymentCardSuccessful: handleAddPaymentCardSuccessful,
                    onAddPaymentCardFailed: handleAddPaymentCardFailed,
                    dispatch
                });
                await client.initialiseConnector();
                dispatch({ type: MemberPaymentCardWalletFormActionType.LoadedPaymentCardsClient, client });
            }
            catch (error) {
                logger.error(`Failed to initialise payment cards connector with ID '${paymentMethodId}'. ${error}`);
                dispatch({
                    type: MemberPaymentCardWalletFormActionType.SetError,
                    error: MemberPaymentCardWalletMessageType.FailedToInitialiseConnector
                });
            }
        })();
    });
    const getLoadingIndicator = () => {
        const loadingIndicatorClass = b.element('loading-indicator');
        switch (loadingState) {
            case MemberPaymentCardWalletFormLoadingState.LoadingConnector:
                return (_jsx(LoadingIndicator, { className: loadingIndicatorClass, iconName: assets.connectorLoadingIconName, type: LoadingIndicatorType.Spinner }));
            case MemberPaymentCardWalletFormLoadingState.AddingCard:
                return (_jsx(LoadingIndicator, { className: loadingIndicatorClass, iconName: assets.processingAddCardIconName, message: addingPaymentCardMessage, type: LoadingIndicatorType.Spinner }));
            default:
                break;
        }
    };
    const isLoading = loadingState !== MemberPaymentCardWalletFormLoadingState.Success;
    const termsAndConditionsCheckboxName = 'TermsAndConditions';
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [getLoadingIndicator(), _jsxs("div", Object.assign({ className: b.element('wrapper', { loading: isLoading }) }, { children: [_jsx("div", { className: b.element('container'), ref: containerRef }), _jsx(CheckboxField, { id: getFieldId(b, termsAndConditionsCheckboxName), name: termsAndConditionsCheckboxName, showLabel: false, value: termsAndConditionsAccepted, disabled: loadingState === MemberPaymentCardWalletFormLoadingState.AddingCard, displayText: { label: addCardTermsAndConditionsFieldLabel }, onChangeValue: handleTermsAndConditionFieldChanged }), _jsxs("div", Object.assign({ className: b.element('button-group') }, { children: [showAddCardButton && (_jsx(Button, { disabled: addPaymentCardButtonState.isDisabled || !termsAndConditionsAccepted, size: ButtonSize.Medium, color: ButtonColor.Primary, label: addCardSubmitButtonLabel, onClick: onAddPaymentCard, loading: loadingState === MemberPaymentCardWalletFormLoadingState.AddingCard })), showCancelButton && (_jsx(Button, { size: ButtonSize.Medium, color: ButtonColor.Secondary, label: promptCancelButtonLabel, onClick: onClose, disabled: loadingState === MemberPaymentCardWalletFormLoadingState.AddingCard }))] }))] }))] })));
});
MemberPaymentCardWalletConnector.displayName = 'MemberPaymentCardWalletConnector';
